import React, { createContext, useContext, useReducer, useEffect, ReactNode, useRef, useCallback, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { TimeEntry, Project, GateResult, WeeklyEntries } from '../types'
import { timerService, timeUtils } from '../services/timerService'
import { projectService } from '../services/projectService'
import { timesheetService, WeekLockStatus } from '../services/timesheetService'
import { checkTimerGates } from '../services/gateService'
import { useAuth } from './AuthContext'
import { widgetCache, CACHE_KEYS } from '@/utils/widgetCache'
import { isAbortError, ApiError } from '@/utils/apiUtils'

// 計時器狀態介面
interface TimerState {
  // 計時器狀態
  activeTimer: TimeEntry | null
  isRunning: boolean
  // elapsedTime 已抽出至獨立 context（TimerElapsedContext），避免每秒 tick 觸發整個 contextValue 重建

  // 專案相關
  projects: Project[]
  selectedProject: Project | null
  recentFavoriteProjects: Project[]
  
  // 工作描述
  description: string
  isDescriptionModified: boolean // 追蹤用戶是否已修改描述
  
  // 今日記錄
  todayEntries: TimeEntry[]
  totalTodayDuration: number

  // 過去 7 天記錄（含今日）— 同時餵給「近期任務建議」與週檢視
  weeklyEntries: WeeklyEntries
  weeklyTotalDuration: number
  // 週資料是否已完成首次載入；初始 {} 是 truthy，無法用來區分「尚未載入」與「真的沒紀錄」
  isWeeklyLoaded: boolean

  // 週次鎖定狀態
  weekLockStatus: WeekLockStatus | null
  isCheckingWeekLock: boolean

  // 統一 Gate 鎖定狀態（過期文件 / 必讀公告 / 個資未填…，見 gate-contract.md）
  gates: GateResult[]
  isCheckingGates: boolean

  // 載入狀態
  isLoading: boolean
  error: string | null
}

// 動作類型
type TimerAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PROJECTS'; payload: Project[] }
  | { type: 'SET_SELECTED_PROJECT'; payload: Project | null }
  | { type: 'SET_RECENT_FAVORITE_PROJECTS'; payload: Project[] }
  | { type: 'SET_DESCRIPTION'; payload: string }
  | { type: 'SET_DESCRIPTION_MODIFIED'; payload: boolean }
  | { type: 'SET_ACTIVE_TIMER'; payload: TimeEntry | null }
  | { type: 'SET_IS_RUNNING'; payload: boolean }
  | { type: 'SET_TODAY_ENTRIES'; payload: { entries: TimeEntry[]; totalDuration: number } }
  | { type: 'ADD_TODAY_ENTRY'; payload: TimeEntry }
  | { type: 'UPDATE_TODAY_ENTRY'; payload: TimeEntry }
  | { type: 'REPLACE_TODAY_ENTRY'; payload: { oldId: string; entry: TimeEntry } }
  | { type: 'REMOVE_TODAY_ENTRY'; payload: string }
  | { type: 'SET_WEEKLY_ENTRIES'; payload: { weeklyEntries: WeeklyEntries; totalDuration: number } }
  | { type: 'SET_WEEKLY_LOADED'; payload: boolean }
  | { type: 'SET_WEEK_LOCK_STATUS'; payload: WeekLockStatus | null }
  | { type: 'SET_CHECKING_WEEK_LOCK'; payload: boolean }
  | { type: 'SET_GATES'; payload: GateResult[] }
  | { type: 'SET_CHECKING_GATES'; payload: boolean }
  | { type: 'RESET_STATE' }

// 初始狀態
const initialState: TimerState = {
  activeTimer: null,
  isRunning: false,
  projects: [],
  selectedProject: null,
  recentFavoriteProjects: [],
  description: '',
  isDescriptionModified: false,
  todayEntries: [],
  totalTodayDuration: 0,
  weeklyEntries: {},
  weeklyTotalDuration: 0,
  isWeeklyLoaded: false,
  weekLockStatus: null,
  isCheckingWeekLock: false,
  gates: [],
  isCheckingGates: false,
  isLoading: false,
  error: null
}

// Reducer 函數
const timerReducer = (state: TimerState, action: TimerAction): TimerState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    
    case 'SET_PROJECTS':
      return { ...state, projects: action.payload }
    
    case 'SET_SELECTED_PROJECT':
      return { ...state, selectedProject: action.payload }
    
    case 'SET_RECENT_FAVORITE_PROJECTS':
      return { ...state, recentFavoriteProjects: action.payload }
    
    case 'SET_DESCRIPTION':
      return { ...state, description: action.payload }
    
    case 'SET_DESCRIPTION_MODIFIED':
      return { ...state, isDescriptionModified: action.payload }
    
    case 'SET_ACTIVE_TIMER':
      return {
        ...state,
        activeTimer: action.payload,
        isRunning: action.payload?.status === 'RUNNING'
      }

    case 'SET_IS_RUNNING':
      return { ...state, isRunning: action.payload }

    case 'SET_TODAY_ENTRIES':
      return { 
        ...state, 
        todayEntries: action.payload.entries,
        totalTodayDuration: action.payload.totalDuration
      }
    
    case 'ADD_TODAY_ENTRY':
      return {
        ...state,
        todayEntries: [action.payload, ...state.todayEntries],
        totalTodayDuration: state.totalTodayDuration + action.payload.duration
      }
    
    case 'UPDATE_TODAY_ENTRY':
      const updatedEntries = state.todayEntries.map(entry =>
        entry.id === action.payload.id ? action.payload : entry
      )
      const newTotalDuration = updatedEntries.reduce((sum, entry) => sum + entry.duration, 0)
      return {
        ...state,
        todayEntries: updatedEntries,
        totalTodayDuration: newTotalDuration
      }

    case 'REPLACE_TODAY_ENTRY': {
      const replaced = state.todayEntries.map(entry =>
        entry.id === action.payload.oldId ? action.payload.entry : entry
      )
      return {
        ...state,
        todayEntries: replaced,
        totalTodayDuration: replaced.reduce((sum, entry) => sum + entry.duration, 0)
      }
    }

    case 'REMOVE_TODAY_ENTRY':
      const filteredEntries = state.todayEntries.filter(entry => entry.id !== action.payload)
      const removedEntry = state.todayEntries.find(entry => entry.id === action.payload)
      return {
        ...state,
        todayEntries: filteredEntries,
        totalTodayDuration: state.totalTodayDuration - (removedEntry?.duration || 0)
      }
    
    case 'SET_WEEKLY_ENTRIES':
      return {
        ...state,
        weeklyEntries: action.payload.weeklyEntries,
        weeklyTotalDuration: action.payload.totalDuration,
        isWeeklyLoaded: true
      }

    case 'SET_WEEKLY_LOADED':
      return { ...state, isWeeklyLoaded: action.payload }

    case 'SET_WEEK_LOCK_STATUS':
      return { ...state, weekLockStatus: action.payload }
    
    case 'SET_CHECKING_WEEK_LOCK':
      return { ...state, isCheckingWeekLock: action.payload }

    case 'SET_GATES':
      return { ...state, gates: action.payload }

    case 'SET_CHECKING_GATES':
      return { ...state, isCheckingGates: action.payload }

    case 'RESET_STATE':
      return initialState
    
    default:
      return state
  }
}

// Context 介面
interface TimerContextType extends TimerState {
  // 動作函數
  loadProjects: (forceReload?: boolean) => Promise<void>
  loadRecentFavoriteProjects: () => Promise<void>
  selectProject: (project: Project | null) => void
  setDescription: (description: string) => void
  startTimer: () => Promise<void>
  quickStartTimer: (project: Project, description: string) => Promise<void>
  stopTimer: () => Promise<void>
  updateTimeEntry: (timeEntryId: string, data: any) => Promise<void>
  deleteTimeEntry: (timeEntryId: string) => Promise<void>
  loadTodayEntries: () => Promise<void>
  loadActiveTimer: () => Promise<void>
  loadWeeklyEntries: () => Promise<void>
  loadTimerBootstrap: () => Promise<void>
  createManualTimeEntry: (data: {
    projectId: string
    description?: string
    startTime: string
    endTime: string
    date: string
    manualReason: string
  }) => Promise<void>
  editTimeEntry: (data: {
    id: string
    projectId: string
    description: string
    startTime: string
    endTime: string
    originalDate?: string
  }) => Promise<TimeEntry>
  checkCurrentWeekLock: () => Promise<void>
  /** force=true 會略過請求去重視窗，供使用者手動「重新檢查」使用 */
  refreshGates: (force?: boolean) => Promise<void>
  refreshTimerData: () => Promise<void>
}

// 建立 Context
const TimerContext = createContext<TimerContextType | undefined>(undefined)

// Provider 組件
interface TimerProviderProps {
  children: ReactNode
}

export const TimerProvider: React.FC<TimerProviderProps> = ({ children }) => {
  // 啟動時 hydrate cache → 首屏直接有 projects / recent favorites，省兩支 API 等待
  // useReducer 第三個參數做 lazy init，只在第一次跑
  const [state, dispatch] = useReducer(timerReducer, initialState, (init) => {
    const cachedProjects = widgetCache.get<Project[]>(CACHE_KEYS.projectsActive)
    const cachedFavorites = widgetCache.get<Project[]>(CACHE_KEYS.recentFavorites)
    return {
      ...init,
      projects: cachedProjects?.value ?? init.projects,
      recentFavoriteProjects: cachedFavorites?.value ?? init.recentFavoriteProjects,
    }
  })
  const { user, token, isLoading: authLoading } = useAuth()

  // elapsedTime 獨立 state — 不放進 reducer 以避免每秒 tick 觸發 contextValue 重建
  const [elapsedTime, setElapsedTime] = useState(0)
  // 給 stopTimer 等 callback 在不需要把 elapsedTime 列為依賴下取得當前值
  const elapsedTimeRef = useRef(0)
  useEffect(() => { elapsedTimeRef.current = elapsedTime }, [elapsedTime])

  // 用於防抖和請求去重的 refs
  const debounceTimerRef = useRef<number | null>(null)
  const loadingRequestsRef = useRef<Set<string>>(new Set())
  const lastLoadTimeRef = useRef<{ [key: string]: number }>({})
  const lastTokenRef = useRef<string | null>(null)
  // 等待中的 startTimer API Promise；用於 stop 時若 start 尚未完成可取得真實 id
  // 失敗時 resolve null（不要 reject，避免 unhandled rejection）
  const pendingStartRef = useRef<Promise<TimeEntry | null> | null>(null)
  // useRef 鏡像 state — 讓 loadActiveTimer / loadTimerBootstrap 的 useCallback
  // 不需要把 state.projects / state.isDescriptionModified 列為 deps，
  // 避免這兩個 state 改變時重建 callback → 重觸發初始化 effect → API burst
  const projectsRef = useRef<Project[]>(state.projects)
  const isDescriptionModifiedRef = useRef<boolean>(state.isDescriptionModified)
  // 每次 render 同步 ref（不觸發 re-render）
  projectsRef.current = state.projects
  isDescriptionModifiedRef.current = state.isDescriptionModified
  
  // 防抖函數
  const debounce = useCallback((func: () => void, delay: number) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(func, delay) as unknown as number
  }, [])
  
  /**
   * 請求去重。
   *
   * requestFn 回傳 false 代表「這次沒真的拿到資料」（失敗或被切頁 abort），
   * 此時必須清掉冷卻時間戳 —— 因為時間戳是在請求「開始」時就寫入的。
   * 若失敗還留著它，會變成：進頁面觸發請求 → 使用者快速切走（GET 被 nav signal
   * abort）→ 切回來時「5 秒內剛請求過」→ 直接 return 不重試 → 資料永遠載不進來、
   * 畫面停在載入中。這正是「快速切換頁面就卡住」的成因。
   *
   * 用回傳值而不是靠例外，是因為底下每支 requestFn 都在自己的 catch 裡把錯誤
   * 吞掉並 dispatch SET_ERROR，例外根本傳不到這一層。
   */
  const withRequestDeduplication = useCallback(async (
    requestKey: string,
    requestFn: () => Promise<void | boolean>,
    minInterval: number = 5000 // 5秒內不重複請求
  ) => {
    // 檢查是否正在請求中
    if (loadingRequestsRef.current.has(requestKey)) {
      return
    }
    
    // 檢查最後請求時間
    const lastLoadTime = lastLoadTimeRef.current[requestKey] || 0
    const now = Date.now()
    if (now - lastLoadTime < minInterval) {
      return
    }
    
    try {
      loadingRequestsRef.current.add(requestKey)
      lastLoadTimeRef.current[requestKey] = now
      const ok = await requestFn()
      if (ok === false) {
        delete lastLoadTimeRef.current[requestKey]
      }
    } catch (error) {
      // requestFn 若真的把例外拋出來，同樣不該佔用冷卻時間
      delete lastLoadTimeRef.current[requestKey]
      throw error
    } finally {
      loadingRequestsRef.current.delete(requestKey)
    }
  }, [])

  // 載入專案列表
  const loadProjects = useCallback(async (forceReload: boolean = false) => {
    if (forceReload) {
      // 強制重新載入時清除該請求的快取
      delete lastLoadTimeRef.current['loadProjects']
      loadingRequestsRef.current.delete('loadProjects')
    }
    
    await withRequestDeduplication('loadProjects', async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true })
        const projects = await projectService.getProjects({ isActive: true })
        dispatch({ type: 'SET_PROJECTS', payload: projects })
        widgetCache.set(CACHE_KEYS.projectsActive, projects)
        dispatch({ type: 'SET_ERROR', payload: null }) // 成功後清除錯誤
      } catch (error) {
        // 切頁 abort 不是真的錯誤，不要用它蓋掉畫面
        if (!isAbortError(error)) {
          dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
        }
        return false // 通知去重層清掉冷卻時間戳，讓下次進頁面能立刻重試
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    })
  }, [withRequestDeduplication])

  // 載入最近常用專案
  const loadRecentFavoriteProjects = useCallback(async () => {
    await withRequestDeduplication('loadRecentFavoriteProjects', async () => {
      try {
        const recentProjects = await projectService.getRecentFavoriteProjects()
        dispatch({ type: 'SET_RECENT_FAVORITE_PROJECTS', payload: recentProjects })
        widgetCache.set(CACHE_KEYS.recentFavorites, recentProjects)
      } catch (error) {
        // 不顯示錯誤，因為這是可選功能
        if (!isAbortError(error)) {
          console.error('載入最近常用專案失敗:', error)
          dispatch({ type: 'SET_RECENT_FAVORITE_PROJECTS', payload: [] })
        }
        return false
      }
    }, 10000) // 10秒內不重複請求
  }, [withRequestDeduplication])

  // 載入今日記錄
  const loadTodayEntries = useCallback(async () => {
    await withRequestDeduplication('loadTodayEntries', async () => {
      try {
        const todayData = await timerService.getTodayEntries()
        dispatch({
          type: 'SET_TODAY_ENTRIES',
          payload: {
            entries: todayData.entries,
            totalDuration: todayData.totalDuration
          }
        })
        dispatch({ type: 'SET_ERROR', payload: null }) // 成功後清除錯誤
      } catch (error) {
        if (!isAbortError(error)) {
          dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
        }
        return false // 讓去重層清掉冷卻時間戳，否則冷卻視窗內切回來不會重試
      }
    }, 3000) // 3秒內不重複請求
  }, [withRequestDeduplication])

  // 載入進行中的計時
  const loadActiveTimer = useCallback(async () => {
    await withRequestDeduplication('loadActiveTimer', async () => {
      try {
        const activeTimer = await timerService.getActiveTimer()
        dispatch({ type: 'SET_ACTIVE_TIMER', payload: activeTimer })

        // 如果有進行中的計時器，自動恢復任務名稱和專案選擇
        if (activeTimer && activeTimer.status === 'RUNNING') {
          // 恢復任務名稱 - 只有在用戶沒有修改過描述的情況下才恢復
          // 使用 ref 讀取最新值，不把 isDescriptionModified 列入 useCallback deps
          if (activeTimer.description && !isDescriptionModifiedRef.current) {
            dispatch({ type: 'SET_DESCRIPTION', payload: activeTimer.description })
          }

          // 恢復專案選擇 - 需要等待專案列表載入完成
          // 使用 ref 讀取最新 projects，不把 projects 列入 useCallback deps
          if (activeTimer.project) {
            const currentProject = projectsRef.current.find(p => p.id === activeTimer.project.id)
            if (currentProject) {
              dispatch({ type: 'SET_SELECTED_PROJECT', payload: currentProject })
            } else {
              dispatch({ type: 'SET_SELECTED_PROJECT', payload: activeTimer.project })
            }
          }
        }

        dispatch({ type: 'SET_ERROR', payload: null }) // 成功後清除錯誤
      } catch (error) {
        if (!isAbortError(error)) {
          dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
        }
        return false
      }
    }, 3000) // 3秒內不重複請求
  // 移除 state.projects / state.isDescriptionModified — 改由 ref 讀取，
  // 避免這兩個 state 改變時重建 callback → 重觸發初始化 effect → API burst
  }, [withRequestDeduplication])

  // 載入過去 7 天記錄 — 給切到週檢視時 refresh 用
  const loadWeeklyEntries = useCallback(async () => {
    await withRequestDeduplication('loadWeeklyEntries', async () => {
      try {
        const data = await timerService.getWeeklyEntries()
        dispatch({
          type: 'SET_WEEKLY_ENTRIES',
          payload: {
            weeklyEntries: data.weeklyEntries,
            totalDuration: data.totalDuration
          }
        })
      } catch (error) {
        // ⛔ 這裡是「快速切頁後週檢視永遠卡在載入中」的直接成因：
        // 原本 abort 時直接 return（undefined），去重層會誤判成成功而保留冷卻時間戳，
        // 3 秒內切回來就不再發請求；而 isWeeklyLoaded 仍是 false，
        // TimeEntryList 會一直渲染 spinner，且沒有任何 effect 會再觸發它
        // （TimerProvider 在 router 之上，不隨路由 remount）。必須回 false。
        if (isAbortError(error)) return false
        console.error('Failed to load weekly entries:', error)
        // 失敗也要解除 loading 狀態，避免週檢視永遠卡 spinner
        dispatch({ type: 'SET_WEEKLY_LOADED', payload: true })
        return false
      }
    }, 3000)
  }, [withRequestDeduplication])

  // 一次取回 active + today + weekly — 計時器首頁初始化用，取代三個獨立請求
  const loadTimerBootstrap = useCallback(async () => {
    await withRequestDeduplication('loadTimerBootstrap', async () => {
      try {
        const data = await timerService.getTimerBootstrap()

        // active timer
        dispatch({ type: 'SET_ACTIVE_TIMER', payload: data.active })
        if (data.active && data.active.status === 'RUNNING') {
          // 同 loadActiveTimer：使用 ref 讀取最新值，不把 state 列入 deps
          if (data.active.description && !isDescriptionModifiedRef.current) {
            dispatch({ type: 'SET_DESCRIPTION', payload: data.active.description })
          }
          if (data.active.project) {
            const currentProject = projectsRef.current.find(p => p.id === data.active!.project.id)
            dispatch({
              type: 'SET_SELECTED_PROJECT',
              payload: currentProject || data.active.project
            })
          }
        }

        // today
        dispatch({
          type: 'SET_TODAY_ENTRIES',
          payload: {
            entries: data.today.entries,
            totalDuration: data.today.totalDuration
          }
        })

        // weekly
        dispatch({
          type: 'SET_WEEKLY_ENTRIES',
          payload: {
            weeklyEntries: data.weekly.weeklyEntries,
            totalDuration: data.weekly.totalDuration
          }
        })

        dispatch({ type: 'SET_ERROR', payload: null })
      } catch (error) {
        // 切頁 abort：不顯示錯誤，但一定要回 false —— 原本這裡直接 return（undefined），
        // 去重層會誤判成「這次成功了」而保留冷卻時間戳，導致切回來的 3 秒內
        // 拒絕重新請求，今日/本週記錄就永遠載不進來。
        if (isAbortError(error)) return false
        dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
        // 失敗也要解除 loading 狀態，避免週檢視永遠卡 spinner
        dispatch({ type: 'SET_WEEKLY_LOADED', payload: true })
        return false
      }
    }, 3000)
  // 移除 state.projects / state.isDescriptionModified — 改由 ref 讀取
  }, [withRequestDeduplication])

  // 選擇專案
  const selectProject = useCallback(async (project: Project | null) => {
    dispatch({ type: 'SET_SELECTED_PROJECT', payload: project })

    // 如果有運行中的計時器，並且切換了專案，則立即更新後端
    if (state.activeTimer && state.isRunning && project && project.id !== state.activeTimer.projectId) {
      try {
        await timerService.updateTimeEntry(state.activeTimer.id, {
          projectId: project.id
        })

        // 更新本地的活動計時器狀態
        const updatedTimer: TimeEntry = {
          ...state.activeTimer,
          projectId: project.id,
          project: project
        }
        dispatch({ type: 'SET_ACTIVE_TIMER', payload: updatedTimer })

        // 重新載入今日記錄以反映變更
        await loadTodayEntries()
      } catch (error) {
        console.error('Failed to update project for running timer:', error)
        dispatch({ type: 'SET_ERROR', payload: '切換專案失敗' })
      }
    }
  }, [state.activeTimer, state.isRunning, loadTodayEntries])

  // 設定工作描述
  const setDescription = useCallback((description: string) => {
    dispatch({ type: 'SET_DESCRIPTION', payload: description })

    // 如果有運行中的計時器且用戶修改了描述，標記為已修改
    if (state.activeTimer && state.isRunning) {
      // 檢查描述是否真的有變更
      if (description !== state.activeTimer.description) {
        dispatch({ type: 'SET_DESCRIPTION_MODIFIED', payload: true })
      }
    }
  }, [state.activeTimer, state.isRunning])

  // 建立樂觀更新用的暫時 TimeEntry
  const buildOptimisticEntry = useCallback((project: Project, description: string): TimeEntry => {
    const now = new Date().toISOString()
    return {
      id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      userId: user?.id || '',
      user: user as any,
      projectId: project.id,
      project,
      description,
      startTime: now,
      endTime: undefined,
      duration: 0,
      isManual: false,
      isEdited: false,
      status: 'RUNNING',
      createdAt: now,
      updatedAt: now
    }
  }, [user])

  // 樂觀啟動：先翻轉 UI，再背景送 API
  const optimisticStart = useCallback((project: Project, description: string): TimeEntry => {
    const tempEntry = buildOptimisticEntry(project, description)
    dispatch({ type: 'SET_ACTIVE_TIMER', payload: tempEntry })
    dispatch({ type: 'ADD_TODAY_ENTRY', payload: tempEntry })
    dispatch({ type: 'SET_ERROR', payload: null })

    pendingStartRef.current = (async (): Promise<TimeEntry | null> => {
      try {
        const real = await timerService.startTimer({
          projectId: project.id,
          description
        })
        // 保留本地 startTime，避免計時讀數與清單顯示時間跳動
        const merged: TimeEntry = { ...real, startTime: tempEntry.startTime }
        dispatch({ type: 'SET_ACTIVE_TIMER', payload: merged })
        dispatch({ type: 'REPLACE_TODAY_ENTRY', payload: { oldId: tempEntry.id, entry: merged } })
        return real
      } catch (error) {
        // 回滾
        dispatch({ type: 'SET_ACTIVE_TIMER', payload: null })
        dispatch({ type: 'REMOVE_TODAY_ENTRY', payload: tempEntry.id })

        // 423：後端在真正啟動當下重新驗證到 gate 鎖定（見 gate-contract.md §3）。
        // 立即把回傳的 gate 灌回 state，讓 banner 不用等下一輪輪詢就能反映最新鎖定原因。
        if (error instanceof ApiError && error.status === 423 && error.gate) {
          dispatch({ type: 'SET_GATES', payload: [error.gate] })
        }

        toast.error((error as Error).message || '開始計時失敗')
        return null
      } finally {
        pendingStartRef.current = null
      }
    })()

    return tempEntry
  }, [buildOptimisticEntry])

  // 開始計時
  const startTimer = useCallback(async () => {
    if (!state.selectedProject) {
      dispatch({ type: 'SET_ERROR', payload: '請選擇專案' })
      return
    }
    optimisticStart(state.selectedProject, state.description)
  }, [state.selectedProject, state.description, optimisticStart])

  // 快速開始計時（直接傳入專案和描述，避免 state 同步問題）
  const quickStartTimer = useCallback(async (project: Project, description: string) => {
    dispatch({ type: 'SET_SELECTED_PROJECT', payload: project })
    dispatch({ type: 'SET_DESCRIPTION', payload: description })
    optimisticStart(project, description)
  }, [optimisticStart])

  // 停止計時
  const stopTimer = useCallback(async () => {
    if (!state.activeTimer) return

    const active = state.activeTimer
    const previousDescription = state.description
    const elapsed = elapsedTimeRef.current
    const now = new Date().toISOString()

    // 樂觀停止：立刻翻轉 UI
    const stoppedOptimistic: TimeEntry = {
      ...active,
      description: previousDescription,
      endTime: now,
      duration: elapsed,
      status: 'STOPPED',
      updatedAt: now
    }

    dispatch({ type: 'SET_ACTIVE_TIMER', payload: null })
    dispatch({ type: 'UPDATE_TODAY_ENTRY', payload: stoppedOptimistic })
    dispatch({ type: 'SET_DESCRIPTION', payload: '' })
    dispatch({ type: 'SET_DESCRIPTION_MODIFIED', payload: false })
    dispatch({ type: 'SET_ERROR', payload: null })

    // 背景送 API
    ;(async () => {
      try {
        let realId = active.id
        // 若 start 還在路上，先等到它回來拿真實 id
        if (active.id.startsWith('temp-')) {
          const realStart = await pendingStartRef.current
          if (!realStart) {
            // start 失敗，已經回滾，不用再做事
            return
          }
          realId = realStart.id
        }

        const realStopped = await timerService.stopTimer(realId, previousDescription)
        if (realStopped.id !== stoppedOptimistic.id) {
          dispatch({ type: 'REPLACE_TODAY_ENTRY', payload: { oldId: stoppedOptimistic.id, entry: realStopped } })
        } else {
          dispatch({ type: 'UPDATE_TODAY_ENTRY', payload: realStopped })
        }
      } catch (error) {
        toast.error((error as Error).message || '停止計時失敗，正在同步')
        // 強制重新同步
        lastLoadTimeRef.current = {}
        loadingRequestsRef.current.clear()
        try {
          await Promise.all([loadActiveTimer(), loadTodayEntries()])
        } catch {
          // ignore
        }
      }
    })()
  }, [state.activeTimer, state.description, loadActiveTimer, loadTodayEntries])

  // 更新計時記錄
  const updateTimeEntry = useCallback(async (timeEntryId: string, data: any) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const updatedTimeEntry = await timerService.updateTimeEntry(timeEntryId, data)
      dispatch({ type: 'UPDATE_TODAY_ENTRY', payload: updatedTimeEntry })
      dispatch({ type: 'SET_ERROR', payload: null })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [])

  // 刪除計時記錄
  const deleteTimeEntry = useCallback(async (timeEntryId: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      await timerService.deleteTimeEntry(timeEntryId)
      dispatch({ type: 'REMOVE_TODAY_ENTRY', payload: timeEntryId })
      dispatch({ type: 'SET_ERROR', payload: null })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [])

  // 手動新增時間記錄
  const createManualTimeEntry = useCallback(async (data: {
    projectId: string
    description?: string
    startTime: string
    endTime: string
    date: string
    manualReason: string
  }) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const newTimeEntry = await timerService.createManualTimeEntry(data)
      
      // 檢查是否是今天的記錄，如果是則添加到今日記錄中
      const today = new Date().toISOString().split('T')[0]
      if (data.date === today) {
        dispatch({ type: 'ADD_TODAY_ENTRY', payload: newTimeEntry })
      }
      
      dispatch({ type: 'SET_ERROR', payload: null })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error // 重新拋出錯誤，讓調用者處理
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [])

  // 編輯時間記錄
  const editTimeEntry = useCallback(async (data: {
    id: string
    projectId: string
    description: string
    startTime: string
    endTime: string
    originalDate?: string // 可選的原始日期，用於週檢視編輯
  }) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      
      let dateString: string
      
      if (data.originalDate) {
        // 如果提供了原始日期，直接使用（來自週檢視）
        dateString = data.originalDate
      } else {
        // 從今日記錄中找到原始記錄以獲取日期（來自今日檢視）
        const originalEntry = state.todayEntries.find(entry => entry.id === data.id)
        if (!originalEntry) {
          throw new Error('找不到原始記錄')
        }
        
        // 從原始記錄獲取本地日期（使用用戶本地時區）
        const originalDate = new Date(originalEntry.startTime)
        const year = originalDate.getFullYear()
        const month = String(originalDate.getMonth() + 1).padStart(2, '0')
        const day = String(originalDate.getDate()).padStart(2, '0')
        dateString = `${year}-${month}-${day}`
      }
      
      const updatedTimeEntry = await timerService.editTimeEntry({
        ...data,
        originalDate: dateString
      })
      
      // 如果是今日記錄，更新今日記錄狀態
      const today = new Date().toISOString().split('T')[0]
      if (dateString === today) {
        dispatch({ type: 'UPDATE_TODAY_ENTRY', payload: updatedTimeEntry })
      }
      
      dispatch({ type: 'SET_ERROR', payload: null })
      
      return updatedTimeEntry
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error // 重新拋出錯誤，讓調用者處理
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [state.todayEntries])

  // 檢查本週鎖定狀態
  const checkCurrentWeekLock = useCallback(async () => {
    await withRequestDeduplication('checkCurrentWeekLock', async () => {
      try {
        dispatch({ type: 'SET_CHECKING_WEEK_LOCK', payload: true })
        // 以 Asia/Taipei 視角取「今天」的日期標籤：避免 client 在 Bangkok/UTC 等時區時
        // toISOString() 的 UTC 日期與 Taipei 日期差一天，導致 server 算出的週次跟其他人錯開
        const taipeiToday = new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().split('T')[0]
        const lockStatus = await timesheetService.checkWeekLock(taipeiToday)
        dispatch({ type: 'SET_WEEK_LOCK_STATUS', payload: lockStatus })
        dispatch({ type: 'SET_ERROR', payload: null }) // 成功後清除錯誤
      } catch (error) {
        // 切頁 abort 不是錯誤，但一樣要回 false 讓去重層清掉冷卻時間戳
        if (!isAbortError(error)) {
          dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
        }
        return false
      } finally {
        dispatch({ type: 'SET_CHECKING_WEEK_LOCK', payload: false })
      }
    }, 3000) // 3秒內不重複請求
  }, [withRequestDeduplication])

  // 檢查統一 Gate 鎖定狀態（過期文件 / 必讀公告 / 個資未填…，見 gate-contract.md）
  // force：使用者手動按「重新檢查」時用。此時若落在去重視窗內會整個不執行，
  // 按鈕不轉也不更新，看起來像壞掉——而這顆按鈕是被鎖住的人唯一的自救入口。
  const refreshGates = useCallback(async (force = false) => {
    if (force) {
      delete lastLoadTimeRef.current['refreshGates']
    }
    await withRequestDeduplication('refreshGates', async () => {
      try {
        dispatch({ type: 'SET_CHECKING_GATES', payload: true })
        const result = await checkTimerGates()
        dispatch({ type: 'SET_GATES', payload: result.gates })
        dispatch({ type: 'SET_ERROR', payload: null })
      } catch (error) {
        // 切頁取消（nav abort）不是錯誤：不印 log、也不要用 fallback 蓋掉現有鎖定狀態。
        // 但要回 false —— 原本直接 return（undefined）會被去重層當成成功而保留冷卻
        // 時間戳，切回來的 5 秒內就不會重查 gate 狀態。
        if (isAbortError(error)) return false
        console.error('檢查計時器鎖定狀態失敗:', error)
        // 發生錯誤時視為無鎖定，避免誤擋用戶（沿用現有「錯誤不擋人」原則）
        dispatch({ type: 'SET_GATES', payload: [] })
        return false
      } finally {
        dispatch({ type: 'SET_CHECKING_GATES', payload: false })
      }
    }, 5000) // 5秒內不重複請求
  }, [withRequestDeduplication])

  // 刷新計時器相關數據
  const refreshTimerData = useCallback(async () => {
    try {
      // 清除防抖和去重緩存，強制重新載入
      lastLoadTimeRef.current = {}
      loadingRequestsRef.current.clear()

      // bootstrap 一次拉回 active + today + weekly；週次鎖定與 gate 並行
      await Promise.all([
        loadTimerBootstrap(),
        checkCurrentWeekLock(),
        refreshGates()
      ])
    } catch (error) {
      console.error('刷新計時器數據失敗:', error)
    }
  }, [loadTimerBootstrap, checkCurrentWeekLock, refreshGates])

  // 當 activeTimer 變動時，依 startTime 重算 elapsedTime
  // 注意：elapsedTime 改用獨立 useState，避免每秒 tick 觸發整個 contextValue 重建
  useEffect(() => {
    if (state.activeTimer) {
      setElapsedTime(timeUtils.calculateDuration(state.activeTimer.startTime))
    } else {
      setElapsedTime(0)
    }
  }, [state.activeTimer])

  // 計時器更新效果
  // 只依賴 isRunning，避免 activeTimer 物件被替換（例如 optimistic → real）時 interval 被重置造成計時跳動
  useEffect(() => {
    if (!state.isRunning) return

    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1)
    }, 1000)

    return () => clearInterval(interval)
  }, [state.isRunning])

  // 監聽 token 變化，當 token 刷新成功後清除錯誤並重新載入數據
  useEffect(() => {
    // 檢查 token 是否有變化（表示可能發生了刷新）
    if (token && lastTokenRef.current && token !== lastTokenRef.current && user && !authLoading) {
      console.log('Token refreshed, clearing error and refreshing timer data...')
      
      // 清除現有的錯誤狀態
      dispatch({ type: 'SET_ERROR', payload: null })
      
      // 刷新計時器數據
      refreshTimerData().catch(error => {
        console.error('Token 刷新後重新載入數據失敗:', error)
      })
    }
    
    // 更新最後的 token 記錄
    lastTokenRef.current = token
  }, [token, user, authLoading, refreshTimerData])

  // 初始化載入 - 只在用戶已登入且身份驗證完成後才載入
  useEffect(() => {
    const initializeTimer = async () => {
      if (!authLoading && user) {
        try {
          // 五支全部並行。loadProjects 原本被 await 卡在前面（理由是「active timer
          // 復原時要查專案 id 對應」），但 loadTimerBootstrap 對此已有 fallback：
          // projectsRef 找不到就直接用 bootstrap 回傳的 project 物件，
          // 而選單是用 id 比對（ProjectSelector.tsx:241），不受物件來源影響。
          // 串行等於讓後面 4 支白等一趟 RTT——跨國連線下就是多等約 0.5 秒。
          await Promise.all([
            loadProjects(),
            loadTimerBootstrap(),
            checkCurrentWeekLock(),
            refreshGates(),
            loadRecentFavoriteProjects()
          ])
        } catch (error: any) {
          console.error('初始化計時器失敗:', error)
          // 如果是認證相關錯誤，不顯示錯誤給用戶
          if (!error.message?.includes('登入已過期') && !error.message?.includes('401')) {
            dispatch({ type: 'SET_ERROR', payload: '載入計時器數據失敗，請重新整理頁面' })
          }
        }
      } else if (!authLoading && !user) {
        // 用戶登出時重置狀態
        dispatch({ type: 'RESET_STATE' })
      }
    }

    initializeTimer()
  }, [user, authLoading, loadProjects, loadTimerBootstrap, checkCurrentWeekLock, refreshGates, loadRecentFavoriteProjects])

  // 定期檢查週次鎖定狀態與過期文件（只在分頁可見時跑）
  useEffect(() => {
    if (!user) return

    let intervalId: ReturnType<typeof setInterval> | null = null

    const tick = () => {
      checkCurrentWeekLock()
      refreshGates()
    }

    const startInterval = () => {
      if (intervalId !== null) return
      intervalId = setInterval(tick, 120000) // 每 2 分鐘檢查一次
    }

    const stopInterval = () => {
      if (intervalId !== null) {
        clearInterval(intervalId)
        intervalId = null
      }
    }

    const handleVisibility = () => {
      if (document.hidden) {
        stopInterval()
      } else {
        tick()
        startInterval()
      }
    }

    if (!document.hidden) startInterval()
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      stopInterval()
    }
  }, [user, checkCurrentWeekLock, refreshGates])

  // 頁面可見性變化時重新載入狀態（使用防抖）
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        debounce(() => {
          loadTimerBootstrap()
        }, 1000) // 1秒防抖
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [user, debounce, loadTimerBootstrap])

  const contextValue: TimerContextType = useMemo(() => ({
    ...state,
    loadProjects,
    loadRecentFavoriteProjects,
    selectProject,
    setDescription,
    startTimer,
    quickStartTimer,
    stopTimer,
    updateTimeEntry,
    deleteTimeEntry,
    loadTodayEntries,
    loadActiveTimer,
    loadWeeklyEntries,
    loadTimerBootstrap,
    createManualTimeEntry,
    editTimeEntry,
    checkCurrentWeekLock,
    refreshGates,
    refreshTimerData
  }), [
    state,
    loadProjects,
    loadRecentFavoriteProjects,
    selectProject,
    setDescription,
    startTimer,
    quickStartTimer,
    stopTimer,
    updateTimeEntry,
    deleteTimeEntry,
    loadTodayEntries,
    loadActiveTimer,
    loadWeeklyEntries,
    loadTimerBootstrap,
    createManualTimeEntry,
    editTimeEntry,
    checkCurrentWeekLock,
    refreshGates,
    refreshTimerData
  ])

  return (
    <TimerContext.Provider value={contextValue}>
      <TimerElapsedContext.Provider value={elapsedTime}>
        {children}
      </TimerElapsedContext.Provider>
    </TimerContext.Provider>
  )
}

// 獨立的 elapsedTime context — 只給需要顯示計時讀數的元件訂閱（每秒會更新）
const TimerElapsedContext = createContext<number>(0)

// Hook：取得當前計時讀數（每秒更新）。
// 一般元件請優先用 useTimer()；只在「需要每秒更新顯示」的元件用此 hook。
export const useTimerElapsed = (): number => {
  return useContext(TimerElapsedContext)
}

// Hook 來使用 Timer Context
export const useTimer = (): TimerContextType => {
  const context = useContext(TimerContext)
  if (context === undefined) {
    throw new Error('useTimer must be used within a TimerProvider')
  }
  return context
}
